// Static seed data for the mock API. Numbers are chosen to match the UI mockups
// (PNGs in software/requirements/UI_mockup/) and to be plausible for a real AU
// residential BESS. Australian conventions throughout: kWh, AUD, DD/MM, NMI.
import type {
  AdmDecision, Battery, BatteryPack, Consent, Device, FirmwareInfo, Inverter, Mode,
  AppNotification, NotificationPrefs, Ownership, Site, Tariff, TouSlot, UiPrefs, User, Weather,
  CommissioningStep, ComplianceRecord, DiagnosticRegister, Authority, ConsentLogEntry, Warranty,
} from './types'

export const seedUser: User = {
  id: 'u-001',
  email: 'homeowner@example.com',
  name: 'Alex Homeowner',
  role: 'homeowner',
}

export const seedSite: Site = {
  id: 'site-001',
  name: 'My Home',
  nmi: '4001 2345 678',
  address: '12 Banksia St, Newcastle NSW 2300',
  dnsp: 'Ausgrid',
  // The demo Residence STATES its own zone, so the mock reports it as stated —
  // the same pair the cloud sends. A fixture naming its own zone is not a guess.
  timeZone: 'Australia/Sydney',
  timeZoneSource: 'residence',
  ratedKwh: 14,
  usableKwh: 13.2,
  moduleCount: 2,
  inverterKw: 10,
}

/**
 * THE RESIDENCE RECORD AS IT IS *STORED* — which is what decides the zone's
 * provenance.
 *
 * `Site.timeZone` on the wire is always present and always usable, but the STORED
 * record behind it may state no zone at all. The cloud resolves one and reports
 * `timeZoneSource` so the two cases stay distinguishable. That resolution has a
 * stored record as its input, so the mock stores one too, rather than hard-coding
 * an answer.
 */
export interface MockResidenceRecord {
  id: string
  name: string
  nmi: string
  address: string
  dnsp: string
  /** The zone the RECORD states. Absent means the record states none — which is a
   *  real stored state, not a missing fixture: `createResidence` derives from the
   *  address and stores nothing when it cannot. */
  statedTimeZone?: string
}

export const seedSiteRecord: MockResidenceRecord = {
  id: seedSite.id,
  name: seedSite.name,
  nmi: seedSite.nmi,
  address: seedSite.address,
  dnsp: seedSite.dnsp,
  statedTimeZone: seedSite.timeZone,
}

/**
 * DEMO RESIDENCES WHOSE ZONE IS *NOT* A STATED FACT (`Site.timeZoneSource`).
 *
 * The cloud reports four provenances for a Residence zone and the seed world only
 * ever produced one of them — `residence`, the single case in which rendering the
 * zone flat is correct. Every other case was therefore unreachable offline, so no
 * hermetic test could see a guessed zone printed as an authoritative one. These
 * fixtures make the other three reachable, keyed by the demo persona that signs
 * into them: the same device world, a different Residence record.
 *
 * `unlinked@example.com` carries no record at all — an account before its home is
 * paired, and the one state in which `GET /site` legitimately fails. It is what
 * makes the Home screen's "no Residence yet" path reachable instead of being
 * papered over by a client-side default zone.
 */
export const seedZoneDemoResidences: Record<string, MockResidenceRecord | null> = {
  // States nothing; the address names WA → derived. A Bunbury home, the exact
  // case that used to render "Australia/Melbourne" as flat fact.
  'perth@example.com': {
    id: 'site-002', name: 'My Home', nmi: '6001 2345 679',
    address: '14 Karri Loop, Bunbury WA 6230', dnsp: 'Western Power',
  },
  // States nothing and the address names no Australian state → named fallback.
  'nozone@example.com': {
    id: 'site-003', name: 'My Home', nmi: '7001 2345 680',
    address: 'Lot 12 Kangaroo Flat Road', dnsp: 'Ausgrid',
  },
  // STATES a zone the platform will not interpret: `Etc/GMT+10` is POSIX
  // sign-inverted (UTC minus ten), so honouring it would run this Queensland
  // home twenty hours out. Reported as `invalid`, never as the home's own.
  'badzone@example.com': {
    id: 'site-004', name: 'My Home', nmi: '3001 2345 681',
    address: '9 Coral St, Cairns QLD 4870', dnsp: 'Energex',
    statedTimeZone: 'Etc/GMT+10',
  },
  'unlinked@example.com': null,
}

export const seedBattery: Battery = {
  soc: 78,
  socKwh: 10.3, // 78% × 13.2 kWh usable
  soh: 99,
  ratedKwh: 14,
  usableKwh: 13.2,
  powerKw: 2.1,
  flow: 'discharging',
  backupReservePct: 20,
  chargeCeilingPct: 100,
  dischargeFloorPct: 10,
  temperatureC: 24,
  cycles: 312,
  warrantedSohPct: 70,
  modules: [
    { id: 'mod-1', label: 'Module 1', soc: 78, soh: 99, voltage: 51.4, temperatureC: 24, status: 'ok' },
    { id: 'mod-2', label: 'Module 2', soc: 77, soh: 99, voltage: 51.2, temperatureC: 25, status: 'balancing' },
  ],
}

/** Expand the site-wide battery state into the two independently identified
 * physical 7 kWh packs required by APP-HOM-10. The small SoC offset represents
 * real pack balancing while the weighted average remains the system SoC. */
export function batteryPacksFromBattery(
  b: Battery,
  warranty: Pick<Warranty, 'holder' | 'registeredAt' | 'endDate' | 'termYears'> = {
    holder: 'Alex Homeowner', registeredAt: '12/03/2026', endDate: '12/03/2036', termYears: 10,
  },
): BatteryPack[] {
  const pack = (
    index: number,
    serial: string,
    socOffset: number,
    powerShare: number,
    totalDischargedKwh: number,
    runningHours: number,
  ): BatteryPack => {
    const module = b.modules[index]
    const soc = Math.max(0, Math.min(100, Math.round((b.soc + socOffset) * 10) / 10))
    const usableKwh = b.usableKwh / 2
    return {
      id: `battery-pack-${index + 1}`,
      name: `Battery Pack ${index + 1}`,
      serial,
      dateAdded: '12 Mar 2026',
      status: module?.status === 'fault' ? 'fault' : 'online',
      ratedKwh: b.ratedKwh / 2,
      usableKwh,
      soc,
      socKwh: Math.round((usableKwh * soc / 100) * 10) / 10,
      soh: module?.soh ?? b.soh,
      powerKw: Math.round((b.powerKw * powerShare) * 100) / 100,
      flow: b.flow,
      voltageV: module?.voltage ?? 51.4,
      chargeLimitKw: 3.5,
      dischargeLimitKw: 3.5,
      maxCellTempC: (module?.temperatureC ?? b.temperatureC) + 6,
      minCellTempC: (module?.temperatureC ?? b.temperatureC) - 1,
      totalDischargedKwh,
      runningHours,
      warranty: { ...warranty },
    }
  }
  return [
    pack(0, 'RSN-B-2451-0098', 0.5, 0.51, 208, 1840),
    pack(1, 'RSN-B-2451-0099', -0.5, 0.49, 204, 1836),
  ]
}

// Self-consumption is the effective (highest-priority active) mode — no higher
// mode is dispatching. Home and Work modes now tell the same story (APP-HOM-05).
export const seedAuthority: Authority = {
  authority: 'economic',
  title: 'Self-consumption',
  detail: 'Using your own solar first — battery is covering the evening shortfall',
  tone: 'orange',
}

// Fixed-precedence order (highest first): backup › TOU › self (APP-HOM-05).
// TOU is user-toggleable; self is the always-on fallback. `active` is computed.
export const seedModes: Mode[] = [
  { id: 'backup', label: 'Backup', icon: 'shield', enabled: false, active: false, description: 'Keep the battery full and ready for outages.' },
  { id: 'tou', label: 'Time of use', icon: 'clock', enabled: false, active: false, description: 'Overrides self-consumption during its windows to cut your bill.' },
  { id: 'self', label: 'Self-use', icon: 'home', enabled: true, active: true, description: 'Always-on fallback — maximise self-consumption of your own solar.' },
]

export const seedTou: TouSlot[] = [
  { id: 'tou-1', action: 'charge', start: '11:00', end: '14:00', powerKw: 2.8, targetSocPct: 90, days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], enabled: true },
  { id: 'tou-2', action: 'discharge', start: '15:00', end: '21:00', powerKw: 3.5, targetSocPct: 20, days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], enabled: true },
]

export const seedTariff: Tariff = {
  type: 'tou',
  retailer: 'AGL',
  plan: 'Solar Savers TOU',
  supplyChargeAud: 1.18,
  feedInAud: 0.05,
  effectiveDate: '01/06/2026',
  periods: [
    { id: 'peak', label: 'Peak', rateAud: 0.52, window: '3–9 pm', colour: 'purple' },
    { id: 'shoulder', label: 'Shoulder', rateAud: 0.28, window: '7 am–3 pm, 9–10 pm', colour: 'yellow' },
    { id: 'offpeak', label: 'Off-peak', rateAud: 0.18, window: '10 pm–7 am', colour: 'green' },
  ],
  // Alternative rate structures the Tariff screen shows when the type is switched.
  flatRateAud: 0.3,
  tiers: [
    { id: 't1', label: 'Tier 1', range: 'First 10 kWh / day', rateAud: 0.25, colour: 'green' },
    { id: 't2', label: 'Tier 2', range: '10–20 kWh / day', rateAud: 0.32 },
    { id: 't3', label: 'Tier 3', range: 'Above 20 kWh / day', rateAud: 0.4, colour: 'orange' },
  ],
  demand: {
    enabled: true,
    fromLabel: '4:00 pm',
    toLabel: '9:00 pm',
    fromHour: 16,
    toHour: 21,
    days: [true, true, true, true, true, false, false],
    daysLabel: 'Weekdays',
    windowLabel: '4–9 pm',
    season: 'All year',
    rateAud: 12.0,
    rateUnit: '/kW·mo',
    peakKw: 4.8,
    peakAt: '12 Jun, 6:12 pm',
    peakPeriodLabel: 'This month',
  },
}

// Display-only home weather block (APP-HOM-27). available:false → degraded state.
export const seedWeather: Weather = {
  available: true,
  tempC: 23,
  condition: 'Clear',
  location: 'Newcastle NSW', // matches seedSite
  sunsetLabel: 'Sunset 6:38 pm',
}

export const seedNotifications: AppNotification[] = [
  { id: 'n-1', category: 'safety', severity: 'critical', title: 'Surge protector needs service', body: 'The Surge Protective Device (SPD) has reached end-of-life. Contact your installer — safety alarms cannot be silenced.', time: 'Today 14:02', read: false, alwaysOn: true },
  { id: 'n-2', category: 'grid', severity: 'info', title: 'Grid export limit applied — Ausgrid', body: 'Ausgrid temporarily limited solar export to 1.5 kW between 11:00 and 13:00 during a grid emergency. No action is needed.', time: 'Today 11:00', read: false },
  { id: 'n-3', category: 'system', severity: 'warning', title: 'Power outage — running on battery', body: 'A grid outage was detected at 09:14. Your home is running on stored energy and solar.', time: 'Today 09:14', read: true },
  { id: 'n-4', category: 'savings', severity: 'info', title: 'You saved $3.80 yesterday', body: 'Using the battery during peak hours reduced grid use by 6.8 kWh.', time: 'Yesterday', read: true },
  { id: 'n-5', category: 'announcement', severity: 'info', title: 'Firmware 2.4.1 available', body: 'Improved time-of-use schedule handling. Your installer can schedule the update.', time: '28/06/2026', read: true },
  { id: 'n-6', category: 'marketing', severity: 'info', title: 'Join a VPP and earn credits', body: 'Opt-in marketing. Risen VPP partners are now live in your area.', time: '24/06/2026', read: true },
]

// Default notification prefs (APP-HOM-08) — mirror of the cloud seed. `safety` is
// locked on and can never be stored false.
export const seedNotificationPrefs: NotificationPrefs = {
  channels: { push: true, email: false, sms: false },
  categories: {
    gridOutage: true, safety: true, vppEvent: true, lowBackupSoc: true,
    firmware: true, sohReport: true, spdStatus: true,
  },
}

export const seedConsents: Consent[] = [
  { id: 'c-1', party: 'DNSP', name: 'Ausgrid — grid export control', status: 'active', revocable: false, revokeVia: 'Required by your grid connection agreement and cannot be withdrawn in the app.', scope: 'Temporarily limit solar export during a grid emergency', grantedAt: 'At connection · 02/06/2026' },
  { id: 'c-2', party: 'VPP', name: 'Origin Loop VPP', status: 'active', revocable: true, revokeVia: 'Disengage through the VPP operator (Origin)', scope: 'Dispatch battery for grid services', grantedAt: '10/06/2026' },
  { id: 'c-3', party: 'Support', name: 'Risen after-sales support', status: 'active', revocable: true, revokeVia: 'Toggle in this centre', scope: 'Read system health to assist with tickets', grantedAt: '02/06/2026' },
  { id: 'c-4', party: 'Marketing', name: 'Product news & offers', status: 'inactive', revocable: true, revokeVia: 'Comms settings', scope: 'Promotional emails & in-app offers', grantedAt: '—' },
]

// Append-only consent activity log (APP-HOM-07). Newest first. Ownership transfer
// appends a "consents reset" + "warranty re-registered" entry (APP-PAIR-07).
export const seedConsentLog: ConsentLogEntry[] = [
  { at: '24 Jun 2026 · 11:20', text: 'Marketing communications opted out', tone: 'muted' },
  { at: '10 Jun 2026 · 09:15', text: 'Origin Loop VPP dispatch consent granted', tone: 'green' },
  { at: '02 Jun 2026 · 16:40', text: 'Risen after-sales support access granted', tone: 'blue' },
  { at: '02 Jun 2026 · 14:02', text: 'DNSP dynamic export & backstop enabled at connection', tone: 'red' },
  { at: '02 Jun 2026 · 13:55', text: 'Account created · owner claim accepted', tone: 'muted' },
]

/** Serials that are installer-held / already-owned and so NOT DIY-claimable — the
 *  owner must use the ownership-REQUEST flow (APP-OWN-05b; one owner per physical
 *  unit, APP-OWN-11). SINGLE SOURCE for the app twin (mirror of
 *  cloud/src/fixtures.ts INSTALLER_HELD_SERIALS): the mock's claim guard reads it,
 *  and OwnerAdd's nearby-unit `owned` flags DERIVE from it (never hand-set). */
export const INSTALLER_HELD_SERIALS = ['RSN-B-2451-0210']

// Battery warranty registration (STR-PRD-08). Holder re-registers to the new owner
// with the physical asset across account pairing changes. Serial/dates match the Battery-pack view.
export const seedWarranty: Warranty = {
  holder: 'Alex Homeowner',
  serial: 'RSN-B-2451-0098',
  registeredAt: '12/03/2026',
  endDate: '12/03/2036',
  termYears: 10,
}

export const seedInverter: Inverter = {
  model: 'Risen RS-10K Hybrid',
  serial: 'INV-2026-004421',
  status: 'Grid-connected · exporting',
  phases: [
    { name: 'L1', voltageV: 240.2, currentA: 4.7, powerKw: 1.13 },
    { name: 'L2', voltageV: 239.6, currentA: 4.6, powerKw: 1.10 },
    { name: 'L3', voltageV: 240.8, currentA: 4.2, powerKw: 1.01 },
  ],
  mppts: [
    { name: 'MPPT 1', voltageV: 384, currentA: 4.1, powerKw: 1.57 },
    { name: 'MPPT 2', voltageV: 372, currentA: 3.2, powerKw: 1.19 },
    { name: 'MPPT 3', voltageV: 168, currentA: 2.9, powerKw: 0.48 },
  ],
  backupOutputKw: 0,
  backupActive: false,
  temperatureC: 41,
  warranty: { years: 10, expires: '06/2036' },
}

// lastContactMs is relative to "now" at seed time for demo APP-HOM-29B honesty
// (online ≈ seconds ago; offline ≈ many minutes ago).
const SEED_NOW = Date.now()

export const seedDevices: Device[] = [
  { id: 'battery-pack-1', kind: 'bess', name: 'Battery Pack 1', detail: '7 kWh battery pack', status: 'online', serial: 'RSN-B-2451-0098', socPct: 78.5, ratedKwh: 7, lastContactMs: SEED_NOW - 8_000, firmwareLabel: 'bms 2.1.0' },
  { id: 'battery-pack-2', kind: 'bess', name: 'Battery Pack 2', detail: '7 kWh battery pack', status: 'online', serial: 'RSN-B-2451-0099', socPct: 77.5, ratedKwh: 7, lastContactMs: SEED_NOW - 12_000, firmwareLabel: 'bms 2.1.0' },
  // Primary site inverter — economic delivery target; normal apply / clamp path.
  { id: 'd-2', kind: 'inverter', name: 'RS-10K Hybrid inverter', detail: 'Grid-connected · 10 kW', status: 'online', serial: 'INV-2026-004421', lastContactMs: SEED_NOW - 5_000, firmwareLabel: 'pcu 1.4.2' },
  // Online second inverter with a flaky Modbus bus (MOCK_UNVERIFIED_DEVICE_ID) so
  // economic fan-out can surface `unverified` without breaking the primary path.
  { id: 'd-2-flaky', kind: 'inverter', name: 'RS-5K West inverter', detail: 'Second string · flaky bus', status: 'online', serial: 'INV-2026-004423', lastContactMs: SEED_NOW - 20_000, firmwareLabel: 'pcu 1.4.1' },
  // Offline inverter — Devices list honesty (APP-HOM-29C); not in economic
  // delivery set while offline.
  { id: 'd-2b', kind: 'inverter', name: 'RS-5K Garage inverter', detail: 'Second string · not reporting', status: 'offline', serial: 'INV-2026-004422', lastContactMs: SEED_NOW - 45 * 60_000, firmwareLabel: 'pcu 1.3.9', faults: ['Comms timeout'] },
  { id: 'd-3', kind: 'meter', name: 'Grid meter (CT)', detail: 'Wired to PCU · phase-correct', status: 'online', lastContactMs: SEED_NOW - 6_000 },
  { id: 'd-4', kind: 'gateway', name: 'Comms module', detail: '4G + Wi-Fi · MQTT to cloud', status: 'online', serial: 'GW-2026-771204', lastContactMs: SEED_NOW - 4_000, firmwareLabel: 'comms 0.9.4' },
  { id: 'd-5', kind: 'evse', name: 'EV charger', detail: 'Not paired · tap to add', status: 'offline' },
]

export const seedOwnership: Ownership = {
  role: 'homeowner',
  owner: { name: 'Alex Homeowner', since: '02/06/2026' },
  members: [
    { name: 'Sam Resident', access: 'Member · view & basic control' },
  ],
  installer: { org: 'Watt Solar & Electrical', access: 'health-only' },
}

// Retained automated-decision records (APP-HOM-22 / CLD-ADM-01): the featured VPP
// dispatch plus recent backstop / optimisation / protection decisions. Time labels
// are static strings (clock-independent) so the retained record is deterministic.
export const seedAdmDecisions: AdmDecision[] = [
  { id: 'adm-1', title: 'Battery discharged to grid', category: 'vpp', whenLabel: 'Today, 6:12 pm', what: 'Exported 2.1 kW for 38 minutes', timeRange: '6:12 – 6:50 pm', why: 'AGL VPP called a dispatch event', dataUsed: 'SoC, export limit, dispatch signal' },
  { id: 'adm-2', title: 'DNSP backstop curtailment', category: 'backstop', whenLabel: 'Yesterday, 1:05 pm', what: 'Export capped to 1.5 kW for 2 hours', timeRange: '1:05 – 3:05 pm', why: 'Ausgrid emergency backstop signal', dataUsed: 'Export limit, backstop signal' },
  { id: 'adm-3', title: 'Off-peak charge optimisation', category: 'optimisation', whenLabel: 'Yesterday, 2:00 am', what: 'Charged to 90% from the grid', timeRange: '2:00 – 4:30 am', why: 'Forecast solar shortfall + cheap off-peak rate', dataUsed: 'Tariff, SoC, weather forecast' },
  { id: 'adm-4', title: 'Fault-protection derate', category: 'protection', whenLabel: '23 Jun', what: 'Output reduced on high temperature', timeRange: '1:20 – 2:10 pm', why: 'Inverter temperature above the derate threshold', dataUsed: 'Inverter temperature, power setpoint' },
]

// Default account-synced Home presentation (APP-HOM-21/23).
export const seedUiPrefs: UiPrefs = {
  theme: 'dark',
  weather: true,
  battery: true,
  energy: true,
  curve: true,
  consumption: false,
  houseStories: 1,
  housePool: false,
  houseRoof: 'gable',
}

// ---- installer seeds ----

export const seedCommissioning: CommissioningStep[] = [
  { id: 's1', title: 'Identify & pair', detail: 'BLE-proximity pair with the uninitiated unit — no site internet needed.', state: 'done' },
  { id: 's2', title: 'Region & grid code', detail: 'Set AS/NZS 4777.2 region (Australia A) and signed grid-parameter set.', state: 'done' },
  { id: 's3', title: 'Export limit', detail: 'Apply the DNSP export limit (Ausgrid · 5 kW fixed).', state: 'now' },
  { id: 's4', title: 'CT polarity self-test', detail: 'Verify current-transformer wiring and phase mapping.', state: 'todo' },
  { id: 's5', title: 'Transfer / island self-test', detail: 'Confirm grid→backup switchover under load.', state: 'todo' },
  { id: 's6', title: 'Registration & claim', detail: 'Provisional ownership; invite homeowner to claim.', state: 'todo' },
  { id: 's-hws', title: 'Hot-water relay (HWS)', detail: 'Declare whether a hot-water-system relay is wired, and set the "hot by" comfort deadline.', state: 'todo' },
  { id: 's7', title: 'Compliance record & handover', detail: 'Auto-generate the AS/NZS 4777.2 commissioning PDF.', state: 'todo' },
]

/** The HWS-declaration step id — the one commissioning step that captures data
 *  (relay connected + comfort deadline) rather than running a self-test
 *  (APP-PAIR-16). Screens match on this id to render the declaration form. */
export const HWS_STEP_ID = 's-hws'

export const seedDiagnostics: DiagnosticRegister[] = [
  {
    group: 'Inverter',
    rows: [
      { label: 'Grid voltage L1/L2/L3', value: '240.2 / 239.6 / 240.8 V', tone: 'ok' },
      { label: 'Grid frequency', value: '49.99 Hz', tone: 'ok' },
      { label: 'Isolation resistance', value: '> 2 MΩ', tone: 'ok' },
      { label: 'Internal temperature', value: '41 °C', tone: 'ok' },
    ],
  },
  {
    group: 'Battery (BMS)',
    rows: [
      { label: 'Pack voltage', value: '102.6 V', tone: 'ok' },
      { label: 'Cell ΔV', value: '14 mV', tone: 'ok' },
      { label: 'Max cell temp', value: '25 °C', tone: 'ok' },
      { label: 'SoC / SoH', value: '78% / 99%', tone: 'ok' },
    ],
  },
  {
    group: 'Protection',
    rows: [
      { label: 'Surge protection (SPD)', value: 'End of life', tone: 'fault' },
      { label: 'RCD self-test', value: 'Pass', tone: 'ok' },
      { label: 'Anti-islanding', value: 'Armed', tone: 'ok' },
    ],
  },
]

export const seedCompliance: ComplianceRecord = {
  evidenceStatus: 'sample',
  site: '12 Banksia St, Newcastle NSW 2300',
  nmi: '4001 2345 678',
  installer: 'J. Rivera · Watt Solar & Electrical',
  accreditation: 'SAA Accred. A1234567',
  date: '01/07/2026',
  region: 'AS/NZS 4777.2 — Australia A',
  exportLimitKw: 5,
  tests: [
    { name: 'CT polarity', result: 'pass', detail: 'Import/export sense correct on all phases' },
    { name: 'Grid-to-backup transfer', result: 'pass', detail: 'Switchover < 20 ms under 2 kW load' },
    { name: 'Anti-islanding', result: 'pass', detail: 'Disconnect within AS/NZS 4777.2 limits' },
    { name: 'Insulation resistance', result: 'pass', detail: '> 2 MΩ' },
  ],
  photos: 6,
}

export const seedFirmware: FirmwareInfo[] = [
  { component: 'Inverter / PCU', slotA: '2.4.1 (active)', slotB: '2.3.8', active: 'A', regionSet: 'AS/NZS 4777.2 AU-A · signed', rolloutStage: 'Staged · 25% cohort' },
  { component: 'BMS', slotA: '1.9.2', slotB: '1.9.0', active: 'A', regionSet: 'n/a', rolloutStage: 'Stable' },
  { component: 'Comms module', slotA: '3.1.0 (active)', slotB: '3.0.6', active: 'A', regionSet: 'n/a', rolloutStage: 'Stable', warranty: '2-year (separate, shorter than the 10-yr battery)' },
]

/** The residences an installer's fleet is lazily seeded with on first view
 *  (Phase 3) — mirror of cloud/src/fixtures.ts. Each becomes a real residence in
 *  the mock's membership maps, so every row drives the live unpair/transfer flows.
 *  `full` rows represent commissioning access; the `health-only` row represents
 *  a completed handoff with basic online/attention/fault status only. */
export const seedResidenceMemberships: {
  label: string
  suburb: string
  address: string
  statusHint: 'healthy' | 'attention' | 'fault'
  access: 'full' | 'health-only'
  /** The outstanding firmware update this Residence carries (APP-INS-05A/05B).
   *  The update plan is per-Residence in the cloud, so it is per-Residence here:
   *  ONE of these homes is mid-commissioning behind a grid-protection release it
   *  must install first, and the next is not. That difference is what makes a
   *  commissioning gate computed against the WRONG Residence visible. */
  outstandingUpdate: 'commissioning-blocking' | 'post-commissioning' | 'none'
}[] = [
  { label: 'T. Nguyen', suburb: 'Maitland', address: '8 Elgin St, Maitland NSW 2320', statusHint: 'healthy', access: 'full', outstandingUpdate: 'commissioning-blocking' },
  { label: 'R. Patel', suburb: 'Charlestown', address: '21 Ridge Rd, Charlestown NSW 2290', statusHint: 'healthy', access: 'full', outstandingUpdate: 'post-commissioning' },
  { label: "M. O'Brien", suburb: 'Cardiff', address: '3 Myall Rd, Cardiff NSW 2285', statusHint: 'fault', access: 'health-only', outstandingUpdate: 'none' },
]
